import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, FileText, Server, Users, Clock, Check, ArrowRight, Scale, Briefcase } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: Lock,
    title: "Client confidentiality",
    description: "WireGuard's ChaCha20 encryption protects attorney-client privileged communications from interception and unauthorized access."
  },
  {
    icon: FileText,
    title: "Secure document access",
    description: "Access case files, contracts, and sensitive legal documents securely from court, home, or client locations."
  },
  {
    icon: Server,
    title: "Multi-office connectivity",
    description: "Connect branch offices, remote attorneys, and co-counsel with encrypted site-to-site VPN tunnels."
  },
  {
    icon: Shield,
    title: "Supports ABA guidelines",
    description: "Help meet American Bar Association cybersecurity guidance and state bar ethics duties for protecting client data."
  },
  {
    icon: Users,
    title: "Team management",
    description: "A centralized dashboard for managing associate, partner, and staff VPN access with role-based permissions."
  },
  {
    icon: Briefcase,
    title: "eDiscovery security",
    description: "Secure transfer of discovery materials and maintain chain of custody for electronically stored information."
  }
];

const useCases = [
  "Remote court appearances and depositions",
  "Secure access to case management systems",
  "Client document portal security",
  "Multi-office firm connectivity",
  "Remote work for attorneys and staff",
  "Encrypted email and messaging in transit",
  "eDiscovery document transfer",
  "Public Wi-Fi protection at courts"
];

const ethicsRequirements = [
  { title: "Competence", desc: "Reasonable security measures for client data" },
  { title: "Confidentiality", desc: "Prevent unauthorized access to communications" },
  { title: "Supervision", desc: "Monitor and manage firm-wide access" },
  { title: "Communication", desc: "Encrypted channels for client communications" }
];

export default function LegalVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Law Firms & Legal Practices | Attorney VPN Security | SACVPN</title>
        <meta
          name="description"
          content="Secure VPN for law firms and legal practices. Protect attorney-client privilege and secure document access with WireGuard encryption, for solo practitioners to large firms."
        />
        <meta
          name="keywords"
          content="law firm VPN, attorney VPN, legal VPN, lawyer cybersecurity, legal document security, attorney-client privilege, legal practice security"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/legal" />
        <meta property="og:title" content="VPN for Law Firms & Legal Practices | Attorney VPN Security | SACVPN" />
        <meta property="og:description" content="Secure VPN for law firms and legal practices. Protect attorney-client privilege and secure document access with WireGuard encryption, for solo practitioners to large firms." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Legal Industry VPN Service",
            "name": "SACVPN Legal VPN Solutions",
            "description": "Secure VPN service for law firms and legal practices, protecting attorney-client privileged communications.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Law Firms and Legal Practices"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Legal
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                Secure VPN for{" "}
                <span className="text-brand-400">law firms</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Protect attorney-client privilege and secure sensitive legal documents. Modern WireGuard encryption for solo practitioners, boutique firms, and large legal practices.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Start free trial
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  Request demo
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Legal security</h3>
                    <p className="text-gray-500 text-sm">Privileged communications protected</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["WireGuard (ChaCha20) encryption", "Supports ABA ethics guidance", "No activity logs", "US-based company"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ethics Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Ethics duties</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Support ABA cybersecurity guidance
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              SACVPN can help your firm address the security expectations in the ABA Model Rules and state bar ethics guidance.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {ethicsRequirements.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <Check className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Built for legal practice security
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Use cases
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Legal practice use cases
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From courtroom to corner office, SACVPN secures the connections across your legal practice.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Why law firms choose SACVPN</h3>
              <div className="space-y-4">
                {[
                  "Simple setup, no IT department required",
                  "Works on all devices (Windows, Mac, iOS, Android)",
                  "Per-attorney or per-device pricing",
                  "US-based email support",
                  "No activity logs, true confidentiality"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Protect your clients' confidentiality
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Secure privileged communications with WireGuard encryption. Start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
